/*
  gc-blocked-domains: silently refuses email-list signups from domains we have
  chosen not to accept, while showing the sender a normal success state.

  Why this exists: two swipe-file harvesters subscribe one address per brand they
  track, purely to collect our campaigns. godigitalpigeon.com encodes the brand
  in the local part (girlchocolate-co@, choclement-com@, pmsgrl-com@ ... on
  inbound.godigitalpigeon.com); ilovemyemail.net does the same with
  plus-addressing (tall.bay386+girlchocolate.co@). Seven profiles between them by
  2026-07-29, newest a day old, so this is ongoing. None of them is a customer.

  Blocking the domain rather than the addresses is what makes this hold: both
  mint a fresh local part per brand, so an address-level block would be stale the
  next time we launch anything.

  Why silent rather than an error message: an error tells the harvester exactly
  which string to change, and they would be back within the day under a new
  label. A success state tells them nothing, so the address they burned stays
  burned. The attempt is beaconed to gc-list-guard instead, which is the
  internal blacklist and the only place the block is visible.

  HOW THE BLOCK WORKS, and why it is not just preventDefault:

  A blocked signup can create a Klaviyo profile through three separate paths,
  and cancelling the form submit only closes one of them.

    1. _learnq.push(['identify', {$email: ...}])
       This ALONE creates a profile. snippets/klaviyo-popup calls it before it
       fires the subscribe request, and the onsite script drains the queue
       independently of any form. The four oldest harvester profiles carry no
       consent record at all, which is what an identify-only profile looks like,
       so this is the path that most likely let them in. Missing this would make
       the whole guard cosmetic.
    2. The subscribe request itself, via fetch or XHR, to Klaviyo.
    3. Shopify's {% form 'customer' %}, which posts server-side to /contact and
       is then synced into Klaviyo by the integration.

  So this file gates the transports (1 and 2) rather than the submit event, and
  handles 3 by cancelling the post and sending the browser to the URL the real
  success banner already renders from. The upshot is that each form runs its own
  success path untouched and looks exactly as it always did, while nothing
  reaches Klaviyo or Shopify.

  Known limit: this covers signups made through a browser. A script POSTing
  straight to Shopify /contact or to Klaviyo's endpoint bypasses it, because
  neither endpoint is ours to gate. gc-list-guard's hourly sweep is what catches
  that case, and it is the reason the sweep exists rather than being belt and
  braces.
*/
(function () {
  'use strict';

  /* Suffix-matched, so a bare domain also covers its subdomains. They use
     inbound.godigitalpigeon.com today and nothing stops them changing that
     label, so block the apex. gc-list-guard serves the authoritative list at
     /domains; this baked-in copy is what makes the guard work on the first
     paint and survive the worker being unreachable. Keep them in step. */
  /* Rule 1: named domains. OBSERVED are harvesters we have actually caught on
     the list. PRE-EMPTIVE are email-monitoring services blocked before they show
     up; those are a judgement call, and the reason it is a cheap one is that no
     retail customer has a personal address at a martech vendor, so a wrong guess
     costs nothing while a right one saves a launch. Keep this array in step with
     email-list-blacklist.json, which find-list-harvesters.py checks. */
  var BLOCKED = [
    /* OBSERVED */
    'godigitalpigeon.com', /* 5 profiles, one per brand, on inbound.* */
    'ilovemyemail.net',    /* 2 profiles, same trick via plus-addressing */
    'santopalacio.com',    /* 4 profiles, one per brand domain, via the footer form, 2026-10-14 and 15 */
    /* PRE-EMPTIVE: competitor-email monitoring and swipe-file archives */
    'milled.com',
    'mailcharts.com',
    'owletter.com',
    'sendview.io',
    'reallygoodemails.com'
  ];

  /* Rule 2: the general one, and the reason this file is not just a list.

     A harvester tracking hundreds of brands HAS to label each address with the
     brand it belongs to, or it cannot sort the mail it collects. Both harvesters
     we caught did it the same way, brand plus TLD in the local part:

       girlchocolate-co@inbound.godigitalpigeon.com
       tall.bay386+choclement.com@ilovemyemail.net

     So we match on that shape instead of on their domain, which catches the next
     one on its first attempt under a name nobody has seen. That is what makes
     this proactive rather than a game of catch-up.

     An explicit separator is required between the name and the TLD. Dropping
     that requirement would start matching plausible fan addresses like
     girlchocolateco@gmail.com, and it would break getchoclement@gmail.com, which
     is OURS. A harvester using no separator at all would slip through; the
     periodic scan in find-list-harvesters.py is the backstop for that, and it
     is why the scan exists rather than being redundant with this. */
  var OUR_DOMAINS = [
    'girlchocolate.co',
    'choclement.com',
    'pmsgrl.com',
    'girlchoc.com',   /* inferred from the harvesters' own labels, not verified */
    'girlchoco.com',  /* same */
    'bhwave.com',
    'bhmetrics.com'
  ];

  var BRAND_TAG = new RegExp(
    OUR_DOMAINS.map(function (d) {
      var parts = d.split('.');
      var name = parts.shift().replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      var tld = parts.join('.').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      return name + '[.\\-_]' + tld;
    }).join('|'),
    'i'
  );

  /* Never let either rule fire on our own mail. Staff and system addresses live
     on these domains and a false positive here would silently drop a real
     signup, which is far worse than letting a harvester through. */
  function isOurDomain(host) {
    for (var i = 0; i < OUR_DOMAINS.length; i++) {
      if (host === OUR_DOMAINS[i] || host.endsWith('.' + OUR_DOMAINS[i])) return true;
    }
    return false;
  }

  var GUARD_ENDPOINT =
    (window.GC_LIST_GUARD && window.GC_LIST_GUARD.endpoint) || '';

  /* Returns a short reason string when the address should be blocked, else null.
     The reason is what gets beaconed, so a future third harvester is
     distinguishable in the log from a hit on a name we already knew. */
  function blockedDomain(value) {
    var raw = String(value || '');
    var at = raw.lastIndexOf('@');
    if (at === -1) return null;
    var host = raw.slice(at + 1).trim().toLowerCase().replace(/\.$/, '');
    if (!host) return null;

    if (isOurDomain(host)) return null;

    for (var i = 0; i < BLOCKED.length; i++) {
      var d = BLOCKED[i];
      if (host === d || host.endsWith('.' + d)) return d;
    }

    /* Rule 2. Test only the local part: our own domain on the right-hand side is
       a normal address, the same string on the left is a brand tag. */
    var local = raw.slice(0, at).trim().toLowerCase();
    if (BRAND_TAG.test(local)) return 'brand-tag:' + host;

    return null;
  }

  /* Pulls any blacklisted address out of an arbitrary request body. The bodies
     differ per transport (URLSearchParams, FormData, JSON, raw string) and we
     care only about whether a blocked address is in there at all, so match on
     the serialised form rather than trying to parse each shape. */
  var ADDRESS = /[A-Z0-9._%+'-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi;

  function findBlocked(text) {
    if (!text) return null;
    var m = String(text).match(ADDRESS);
    if (!m) return null;
    for (var i = 0; i < m.length; i++) {
      /* Percent-encoded bodies turn %40 back into @ before the regex sees it,
         but a decode can still throw on malformed input, so guard it. */
      if (blockedDomain(m[i])) return m[i];
    }
    return null;
  }

  function bodyToText(body) {
    if (!body) return '';
    try {
      if (typeof body === 'string') return decodeURIComponent(body.replace(/\+/g, ' '));
      if (body instanceof URLSearchParams) return body.toString();
      if (typeof FormData !== 'undefined' && body instanceof FormData) {
        var out = [];
        body.forEach(function (v, k) { out.push(k + '=' + v); });
        return out.join('&');
      }
      return String(body);
    } catch (e) {
      try { return String(body); } catch (e2) { return ''; }
    }
  }

  /* Endpoints that create or subscribe a profile. Deliberately a substring list
     rather than exact URLs: Klaviyo has moved these paths before and a missed
     path fails open. */
  var SIGNUP_ENDPOINTS = [
    'manage.kmail-lists.com',
    'a.klaviyo.com/client/subscriptions',
    'a.klaviyo.com/client/profiles',
    'a.klaviyo.com/api/track',
    'a.klaviyo.com/api/identify',
    'klaviyo.com/onsite',
    '/contact'
  ];

  function isSignupUrl(url) {
    var u = String(url || '');
    for (var i = 0; i < SIGNUP_ENDPOINTS.length; i++) {
      if (u.indexOf(SIGNUP_ENDPOINTS[i]) !== -1) return true;
    }
    return false;
  }

  var reported = {};

  /* Fire and forget. sendBeacon survives the page unload that a cancelled
     Shopify form turns into, which plain fetch does not. Never surfaces an
     error: a failed beacon must not change what the visitor sees, because the
     whole point is that the block is invisible. */
  function report(email, domain, path) {
    if (!GUARD_ENDPOINT || reported[email]) return;
    reported[email] = true;
    var payload = JSON.stringify({
      email: email,
      domain: domain,
      path: path || '',
      page: location.pathname,
      ts: new Date().toISOString()
    });
    try {
      if (navigator.sendBeacon) {
        navigator.sendBeacon(GUARD_ENDPOINT, new Blob([payload], { type: 'text/plain' }));
        return;
      }
    } catch (e) {}
    try {
      /* keepalive so it still goes out if the page is navigating away. */
      window.__gcRealFetch(GUARD_ENDPOINT, {
        method: 'POST', body: payload, keepalive: true, mode: 'no-cors'
      }).catch(function () {});
    } catch (e) {}
  }

  /* A response shaped like a success so the caller's .then branch runs and its
     success UI appears. Klaviyo's own forms check for a JSON body. */
  function fakeOk() {
    try {
      return Promise.resolve(new Response('{"success":true,"data":[]}', {
        status: 200, headers: { 'Content-Type': 'application/json' }
      }));
    } catch (e) {
      return Promise.resolve({ ok: true, status: 200, json: function () { return Promise.resolve({ success: true }); }, text: function () { return Promise.resolve('{"success":true}'); } });
    }
  }

  /* ---------- transport 2a: fetch ---------- */
  var realFetch = window.fetch && window.fetch.bind(window);
  window.__gcRealFetch = realFetch;
  if (realFetch) {
    window.fetch = function (input, init) {
      try {
        var url = (input && input.url) || input;
        if (isSignupUrl(url)) {
          var body = bodyToText((init && init.body) || (input && input.body));
          var hit = findBlocked(body) || findBlocked(url);
          if (hit) {
            report(hit, blockedDomain(hit), String(url));
            return fakeOk();
          }
        }
      } catch (e) { /* never break a real request */ }
      return realFetch(input, init);
    };
  }

  /* ---------- transport 2b: XMLHttpRequest ---------- */
  if (window.XMLHttpRequest) {
    var XHR = window.XMLHttpRequest;
    var open = XHR.prototype.open;
    var send = XHR.prototype.send;
    XHR.prototype.open = function (method, url) {
      try { this.__gcUrl = url; } catch (e) {}
      return open.apply(this, arguments);
    };
    XHR.prototype.send = function (body) {
      try {
        if (isSignupUrl(this.__gcUrl)) {
          var hit = findBlocked(bodyToText(body)) || findBlocked(this.__gcUrl);
          if (hit) {
            report(hit, blockedDomain(hit), String(this.__gcUrl));
            var self = this;
            /* Synthesise a completed 200 so the caller's onload/onreadystatechange
               success branch runs and its UI updates as normal. */
            setTimeout(function () {
              try {
                Object.defineProperty(self, 'readyState', { value: 4, configurable: true });
                Object.defineProperty(self, 'status', { value: 200, configurable: true });
                Object.defineProperty(self, 'responseText', { value: '{"success":true}', configurable: true });
                Object.defineProperty(self, 'response', { value: '{"success":true}', configurable: true });
              } catch (e) {}
              try { self.onreadystatechange && self.onreadystatechange(); } catch (e) {}
              try { self.dispatchEvent(new Event('load')); } catch (e) {}
              try { self.onload && self.onload(); } catch (e) {}
              try { self.dispatchEvent(new Event('loadend')); } catch (e) {}
            }, 30);
            return;
          }
        }
      } catch (e) {}
      return send.apply(this, arguments);
    };
  }

  /* ---------- transport 1: the Klaviyo onsite queue ----------
     _learnq.push(['identify', {$email}]) creates a profile by itself, so this
     is the path that matters most. The queue starts life as a plain array and
     klaviyo.js later swaps in its own object, which would drop a wrapper we
     installed on the original. The accessor pair below re-wraps whatever gets
     assigned, so the filter survives that handover in either order. */
  function wrapQueue(q) {
    if (!q || q.__gcWrapped) return q;
    var realPush = q.push;
    try {
      q.push = function () {
        var kept = [];
        for (var i = 0; i < arguments.length; i++) {
          var entry = arguments[i];
          var hit = null;
          try { hit = findBlocked(JSON.stringify(entry)); } catch (e) {}
          if (hit) {
            report(hit, blockedDomain(hit), '_learnq');
            continue; /* drop it, and report nothing back to the caller */
          }
          kept.push(entry);
        }
        return kept.length ? realPush.apply(q, kept) : q.length;
      };
      q.__gcWrapped = true;
    } catch (e) {}
    return q;
  }

  try {
    var queue = wrapQueue(window._learnq || []);
    Object.defineProperty(window, '_learnq', {
      configurable: true,
      get: function () { return queue; },
      set: function (v) { queue = wrapQueue(v); }
    });
  } catch (e) {
    window._learnq = wrapQueue(window._learnq || []);
  }

  /* Newer onsite builds expose klaviyo.identify() / .push() directly rather
     than going through the queue. Same filter, applied whenever it appears. */
  function wrapKlaviyoObject(k) {
    if (!k || k.__gcWrapped) return k;
    ['identify', 'push', 'track'].forEach(function (fn) {
      if (typeof k[fn] !== 'function') return;
      var real = k[fn].bind(k);
      k[fn] = function () {
        try {
          var hit = findBlocked(JSON.stringify(Array.prototype.slice.call(arguments)));
          if (hit) {
            report(hit, blockedDomain(hit), 'klaviyo.' + fn);
            return Promise.resolve ? Promise.resolve() : undefined;
          }
        } catch (e) {}
        return real.apply(null, arguments);
      };
    });
    try { k.__gcWrapped = true; } catch (e) {}
    return k;
  }

  try {
    var kobj = wrapKlaviyoObject(window.klaviyo);
    Object.defineProperty(window, 'klaviyo', {
      configurable: true,
      get: function () { return kobj; },
      set: function (v) { kobj = wrapKlaviyoObject(v); }
    });
  } catch (e) {}

  /* ---------- transport 3: Shopify's native customer form ----------
     This one posts server-side with a full page navigation, so there is no
     request for the transport hooks above to intercept. Cancel it and go to the
     URL the theme's own success banner renders from, which is the same thing the
     visitor would have seen had it worked. */
  document.addEventListener(
    'submit',
    function (event) {
      var form = event.target;
      if (!form || form.tagName !== 'FORM') return;

      var fields = form.querySelectorAll(
        'input[type="email"], input[name="email"], input[name*="[email]"]'
      );
      var hit = null;
      for (var i = 0; i < fields.length && !hit; i++) {
        if (blockedDomain(fields[i].value)) hit = fields[i].value;
      }
      if (!hit) return;

      report(hit, blockedDomain(hit), 'form-submit');

      /* Only take over forms that would leave the page. An AJAX or popup form
         is left alone on purpose: its own handler runs, shows its own success
         state, and the transport hooks have already dropped the request. */
      var isCustomerForm = !!form.querySelector('input[name="form_type"][value="customer"]');
      var ajax = form.hasAttribute('data-popup-form') ||
                 form.getAttribute('data-ajax') === 'true';
      if (!isCustomerForm || ajax) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      var sep = location.search ? '&' : '?';
      location.href = location.pathname + location.search + sep +
                      'customer_posted=true' + location.hash;
    },
    true /* capture, so we decide before any form's own handler binds */
  );
})();
